import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  Pressable,
  ActivityIndicator,
  Alert,
  ScrollView,
  RefreshControl,
  Modal,
  TextInput,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Team, Profile } from "@/types";

/**
 * Team member with profile data
 */
interface TeamMember {
  id: string;
  user_id: string;
  is_captain: boolean;
  joined_at: string;
  profile: Pick<Profile, "id" | "full_name" | "avatar_url" | "email" | "is_dependent" | "guardian_ids">;
}

interface PendingMemberRow {
  id: string;
  email: string;
  full_name: string;
  team_id: string | null;
  claimed_at: string | null;
}

/**
 * Member Management Screen
 */
export default function MemberManagementScreen() {
  const router = useRouter();
  const { id: teamId } = useLocalSearchParams<{ id: string }>();
  const { activeWeekend, user } = useAuth();
  const colors = useThemeColors();

  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<Pick<Profile, "id" | "full_name" | "avatar_url" | "email">[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showGuardianModal, setShowGuardianModal] = useState(false);
  const [selectedDependent, setSelectedDependent] = useState<TeamMember | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [isSavingGuardians, setIsSavingGuardians] = useState(false);
  const [assignedPending, setAssignedPending] = useState<PendingMemberRow[]>([]);
  const [availablePending, setAvailablePending] = useState<PendingMemberRow[]>([]);
  const [isAssigningPending, setIsAssigningPending] = useState<string | null>(null);

  /**
   * Fetch team and members
   */
  const fetchData = useCallback(async () => {
    if (!teamId || !activeWeekend) return;

    try {
      // Fetch team
      const { data: teamData, error: teamError } = await supabase
        .from("teams")
        .select("*")
        .eq("id", teamId)
        .single();

      if (teamError) throw teamError;
      setTeam(teamData);

      // Fetch team members with profiles
      const { data: membersData, error: membersError } = await supabase
        .from("team_members")
        .select(`
          id,
          user_id,
          is_captain,
          joined_at,
          profiles:user_id (
            id,
            full_name,
            avatar_url,
            email,
            is_dependent,
            guardian_ids
          )
        `)
        .eq("team_id", teamId)
        .order("is_captain", { ascending: false });

      if (membersError) throw membersError;

      // Transform data
      const transformedMembers: TeamMember[] = (membersData || []).map((m: any) => ({
        id: m.id,
        user_id: m.user_id,
        is_captain: m.is_captain,
        joined_at: m.joined_at,
        profile: m.profiles,
      }));

      setMembers(transformedMembers);

      // Get all team members for this weekend (to exclude from available)
      const { data: allTeamMembers } = await supabase
        .from("team_members")
        .select("user_id, teams!inner(weekend_id)")
        .eq("teams.weekend_id", activeWeekend.id);

      const assignedUserIds = allTeamMembers?.map((tm: any) => tm.user_id) || [];

      // Fetch all profiles (excluding already assigned)
      const { data: allProfiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, email, is_dependent, guardian_ids")
        .order("full_name");

      if (profilesError) throw profilesError;

      // Filter available users
      const available = (allProfiles || []).filter(
        (p) => !assignedUserIds.includes(p.id)
      );

      setAvailableUsers(available);

      // Fetch unclaimed pending members for this weekend
      const { data: pendingData } = await supabase
        .from("pending_members")
        .select("id, email, full_name, team_id, claimed_at")
        .eq("weekend_id", activeWeekend.id)
        .is("claimed_at", null)
        .order("full_name");

      const allPending = (pendingData || []) as PendingMemberRow[];
      setAssignedPending(allPending.filter((p) => p.team_id === teamId));
      setAvailablePending(allPending.filter((p) => p.team_id === null));
    } catch (error) {
      console.error("Error fetching data:", error);
      Alert.alert("Fout", "Kon gegevens niet laden");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [teamId, activeWeekend]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  /**
   * Add member to team
   */
  const handleAddMember = async (profile: Pick<Profile, "id" | "full_name" | "avatar_url" | "email">) => {
    if (!user || !teamId) return;

    setIsAdding(profile.id);

    try {
      const { error } = await supabase.from("team_members").insert({
        team_id: teamId,
        user_id: profile.id,
        added_by: user.id,
      });

      if (error) throw error;

      // Update local state
      setAvailableUsers((prev) => prev.filter((p) => p.id !== profile.id));
      setMembers((prev) => [
        ...prev,
        {
          id: `temp-${profile.id}`,
          user_id: profile.id,
          is_captain: false,
          joined_at: new Date().toISOString(),
          profile: profile as any,
        },
      ]);

      // Refresh to get actual data
      fetchData();
    } catch (error: any) {
      console.error("Error adding member:", error);
      Alert.alert("Fout", error.message || "Kon lid niet toevoegen");
    } finally {
      setIsAdding(null);
    }
  };

  /**
   * Remove member from team
   */
  const handleRemoveMember = (member: TeamMember) => {
    Alert.alert(
      "Lid Verwijderen",
      `Weet je zeker dat je ${member.profile.full_name || "dit lid"} wilt verwijderen uit het team?`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            try {
              const { error } = await supabase
                .from("team_members")
                .delete()
                .eq("id", member.id);

              if (error) throw error;

              // Update local state
              setMembers((prev) => prev.filter((m) => m.id !== member.id));
              setAvailableUsers((prev) => [...prev, member.profile as Profile]);
            } catch (error: any) {
              console.error("Error removing member:", error);
              Alert.alert("Fout", error.message || "Kon lid niet verwijderen");
            }
          },
        },
      ]
    );
  };

  /**
   * Open guardian assignment modal for a dependent
   */
  const handleOpenGuardianModal = (member: TeamMember) => {
    setSelectedDependent(member);
    setShowGuardianModal(true);
  };

  /**
   * Toggle guardian for a dependent
   */
  const handleToggleGuardian = async (guardianId: string) => {
    if (!selectedDependent) return;

    const currentGuardians = selectedDependent.profile.guardian_ids || [];
    const isCurrentlyGuardian = currentGuardians.includes(guardianId);
    
    let newGuardians: string[];
    if (isCurrentlyGuardian) {
      newGuardians = currentGuardians.filter((id) => id !== guardianId);
    } else {
      newGuardians = [...currentGuardians, guardianId];
    }

    setIsSavingGuardians(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ guardian_ids: newGuardians })
        .eq("id", selectedDependent.profile.id);

      if (error) throw error;

      // Update local state
      setMembers((prev) =>
        prev.map((m) =>
          m.id === selectedDependent.id
            ? {
                ...m,
                profile: { ...m.profile, guardian_ids: newGuardians },
              }
            : m
        )
      );

      // Update selected dependent
      setSelectedDependent((prev) =>
        prev
          ? {
              ...prev,
              profile: { ...prev.profile, guardian_ids: newGuardians },
            }
          : null
      );
    } catch (error: any) {
      console.error("Error updating guardians:", error);
      Alert.alert("Fout", error.message || "Kon guardians niet bijwerken");
    } finally {
      setIsSavingGuardians(false);
    }
  };

  /**
   * Toggle captain status
   */
  const handleToggleCaptain = async (member: TeamMember) => {
    try {
      // If making this person captain, remove captain from others first
      if (!member.is_captain) {
        await supabase
          .from("team_members")
          .update({ is_captain: false })
          .eq("team_id", teamId);
      }

      const { error } = await supabase
        .from("team_members")
        .update({ is_captain: !member.is_captain })
        .eq("id", member.id);

      if (error) throw error;

      // Update local state
      setMembers((prev) =>
        prev.map((m) => ({
          ...m,
          is_captain: m.id === member.id ? !member.is_captain : false,
        }))
      );
    } catch (error: any) {
      console.error("Error toggling captain:", error);
      Alert.alert("Fout", error.message || "Kon aanvoerder niet wijzigen");
    }
  };

  /**
   * Assign a pending member to this team
   */
  const handleAssignPending = async (pending: PendingMemberRow) => {
    if (!teamId) return;
    setIsAssigningPending(pending.id);

    try {
      const { error } = await supabase
        .from("pending_members")
        .update({ team_id: teamId })
        .eq("id", pending.id);

      if (error) throw error;

      setAvailablePending((prev) => prev.filter((p) => p.id !== pending.id));
      setAssignedPending((prev) => [...prev, { ...pending, team_id: teamId }]);
    } catch (error: any) {
      console.error("Error assigning pending member:", error);
      Alert.alert("Fout", error.message || "Kon pre-registratie niet toewijzen");
    } finally {
      setIsAssigningPending(null);
    }
  };

  /**
   * Remove a pending member from this team
   */
  const handleUnassignPending = (pending: PendingMemberRow) => {
    Alert.alert(
      "Pre-registratie Verwijderen",
      `Weet je zeker dat je ${pending.full_name} wilt verwijderen uit het team?`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: async () => {
            try {
              const { error } = await supabase
                .from("pending_members")
                .update({ team_id: null })
                .eq("id", pending.id);

              if (error) throw error;

              setAssignedPending((prev) => prev.filter((p) => p.id !== pending.id));
              setAvailablePending((prev) => [...prev, { ...pending, team_id: null }]);
            } catch (error: any) {
              console.error("Error unassigning pending member:", error);
              Alert.alert("Fout", error.message || "Kon pre-registratie niet verwijderen");
            }
          },
        },
      ]
    );
  };

  /**
   * Filter available users by search query
   */
  const filteredAvailableUsers = availableUsers.filter((profile) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      profile.full_name?.toLowerCase().includes(query) ||
      profile.email?.toLowerCase().includes(query)
    );
  });

  const filteredAvailablePending = availablePending.filter((pending) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      pending.full_name?.toLowerCase().includes(query) ||
      pending.email?.toLowerCase().includes(query)
    );
  });

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color="#D97706" />
        <Text className="mt-2" style={{ color: "#D97706" }}>
          Laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!team) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="alert-circle-outline" size={48} color="#D97706" />
        <Text className="mt-2" style={{ color: "#D97706" }}>
          Team niet gevonden
        </Text>
      </SafeAreaView>
    );
  }

  const teamColor = team.color || "#7C3AED";

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      {/* Team Header */}
      <View
        className="px-4 py-3"
        style={{
          backgroundColor: colors.surface,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        }}
      >
        <View className="flex-row items-center">
          <View
            className="w-12 h-12 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: `${teamColor}20` }}
          >
            <Text className="text-xl font-bold" style={{ color: teamColor }}>
              {team.name.charAt(0).toUpperCase()}
            </Text>
          </View>
          <View className="flex-1">
            <Text className="text-lg font-semibold" style={{ color: colors.text }}>
              {team.name}
            </Text>
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {members.length} {members.length === 1 ? "lid" : "leden"}
              {assignedPending.length > 0 && ` · ${assignedPending.length} pre-registratie${assignedPending.length !== 1 ? "s" : ""}`}
            </Text>
          </View>
          <Pressable
            onPress={() => setShowAddModal(true)}
            className="bg-amber-500 rounded-xl px-4 py-2 flex-row items-center active:bg-amber-600"
            accessibilityRole="button"
            accessibilityLabel="Lid toevoegen"
          >
            <Ionicons name="person-add-outline" size={18} color="#FFFFFF" />
            <Text className="text-white font-medium ml-1">Toevoegen</Text>
          </Pressable>
        </View>
      </View>

      <ScrollView
        className="flex-1 px-4 pt-4"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor="#D97706"
          />
        }
      >
        {/* Members List */}
        {members.length > 0 || assignedPending.length > 0 ? (
          <View
            className="rounded-xl overflow-hidden mb-4"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            {members.map((member, index) => {
              const isDependent = member.profile.is_dependent === true;
              const guardianCount = member.profile.guardian_ids?.length || 0;
              const isLast = index === members.length - 1 && assignedPending.length === 0;

              return (
                <View
                  key={member.id}
                  className="flex-row items-center px-4 py-3"
                  style={{
                    borderBottomWidth: isLast ? 0 : 1,
                    borderBottomColor: colors.border,
                  }}
                >
                  {/* Avatar */}
                  <View
                    className="w-12 h-12 rounded-full items-center justify-center mr-3"
                    style={{ backgroundColor: isDependent ? "#DBEAFE" : `${teamColor}15` }}
                  >
                    {member.is_captain && (
                      <View className="absolute -top-1 -right-1 bg-amber-400 rounded-full p-0.5">
                        <Ionicons name="star" size={12} color="#FFFFFF" />
                      </View>
                    )}
                    <Text className="font-medium" style={{ color: isDependent ? "#1D4ED8" : teamColor }}>
                      {member.profile.full_name?.charAt(0).toUpperCase() || "?"}
                    </Text>
                  </View>

                  {/* Info */}
                  <View className="flex-1">
                    <View className="flex-row items-center flex-wrap">
                      <Text className="font-medium" style={{ color: colors.text }}>
                        {member.profile.full_name || "Onbekend"}
                      </Text>
                      {member.is_captain && (
                        <View className="bg-amber-100 rounded px-1.5 py-0.5 ml-2">
                          <Text className="text-amber-700 text-xs font-medium">
                            Aanvoerder
                          </Text>
                        </View>
                      )}
                      {isDependent && (
                        <View className="rounded px-1.5 py-0.5 ml-2" style={{ backgroundColor: "#DBEAFE" }}>
                          <Text style={{ color: "#1D4ED8" }} className="text-xs font-medium">
                            Kind
                          </Text>
                        </View>
                      )}
                    </View>
                    <Text className="text-sm" style={{ color: colors.textTertiary }}>
                      {isDependent
                        ? guardianCount > 0
                          ? `${guardianCount} guardian${guardianCount > 1 ? "en" : ""}`
                          : "Geen guardian toegewezen"
                        : member.profile.email}
                    </Text>
                  </View>

                  {/* Actions */}
                  <View className="flex-row items-center">
                    {isDependent && (
                      <Pressable
                        onPress={() => handleOpenGuardianModal(member)}
                        className="p-2 mr-1"
                        accessibilityRole="button"
                        accessibilityLabel="Guardians beheren"
                      >
                        <Ionicons
                          name="people-outline"
                          size={20}
                          color={guardianCount > 0 ? "#1D4ED8" : "#EF4444"}
                        />
                      </Pressable>
                    )}
                    {!isDependent && (
                      <Pressable
                        onPress={() => handleToggleCaptain(member)}
                        className="p-2 mr-1"
                        accessibilityRole="button"
                        accessibilityLabel={member.is_captain ? "Aanvoerder verwijderen" : "Maak aanvoerder"}
                      >
                        <Ionicons
                          name={member.is_captain ? "star" : "star-outline"}
                          size={20}
                          color={member.is_captain ? "#F59E0B" : colors.textTertiary}
                        />
                      </Pressable>
                    )}
                    <Pressable
                      onPress={() => handleRemoveMember(member)}
                      className="p-2"
                      accessibilityRole="button"
                      accessibilityLabel="Lid verwijderen"
                    >
                      <Ionicons name="close-circle-outline" size={20} color="#EF4444" />
                    </Pressable>
                  </View>
                </View>
              );
            })}

            {/* Assigned Pending Members */}
            {assignedPending.map((pending, index) => (
              <View
                key={`pending-${pending.id}`}
                className="flex-row items-center px-4 py-3"
                style={{
                  borderBottomWidth: index < assignedPending.length - 1 ? 1 : 0,
                  borderBottomColor: colors.border,
                  backgroundColor: "#FFFBEB",
                }}
              >
                <View
                  className="w-12 h-12 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: "#FEF3C7" }}
                >
                  <Ionicons name="hourglass-outline" size={20} color="#D97706" />
                </View>
                <View className="flex-1">
                  <View className="flex-row items-center flex-wrap">
                    <Text className="font-medium" style={{ color: colors.text }}>
                      {pending.full_name}
                    </Text>
                    <View className="rounded px-1.5 py-0.5 ml-2" style={{ backgroundColor: "#FEF3C7" }}>
                      <Text style={{ color: "#D97706" }} className="text-xs font-medium">
                        Pre-registratie
                      </Text>
                    </View>
                  </View>
                  <Text className="text-sm" style={{ color: colors.textTertiary }}>
                    {pending.email}
                  </Text>
                </View>
                <Pressable
                  onPress={() => handleUnassignPending(pending)}
                  className="p-2"
                  accessibilityRole="button"
                  accessibilityLabel="Pre-registratie verwijderen uit team"
                >
                  <Ionicons name="close-circle-outline" size={20} color="#EF4444" />
                </Pressable>
              </View>
            ))}
          </View>
        ) : (
          <View
            className="rounded-xl p-8 items-center mb-4"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <Ionicons name="people-outline" size={48} color={colors.textTertiary} />
            <Text className="mt-2 text-center" style={{ color: colors.textTertiary }}>
              Nog geen teamleden
            </Text>
            <Pressable
              onPress={() => setShowAddModal(true)}
              className="mt-4 bg-amber-100 rounded-lg px-4 py-2"
              accessibilityRole="button"
              accessibilityLabel="Voeg leden toe"
            >
              <Text className="text-amber-700 font-medium">Voeg leden toe</Text>
            </Pressable>
          </View>
        )}

        {/* Legend */}
        <View
          className="rounded-xl p-4 mb-4"
          style={{ backgroundColor: "#FEF3C7" }}
        >
          <View className="flex-row items-center mb-2">
            <Ionicons name="star" size={16} color="#F59E0B" />
            <Text className="text-sm ml-2" style={{ color: "#78350F" }}>
              Klik op de ster om een aanvoerder aan te wijzen
            </Text>
          </View>
          <View className="flex-row items-center mb-2">
            <Ionicons name="people-outline" size={16} color="#1D4ED8" />
            <Text className="text-sm ml-2" style={{ color: "#78350F" }}>
              Klik op personen-icoon om guardians te beheren (bij kinderen)
            </Text>
          </View>
          <View className="flex-row items-center mb-2">
            <Ionicons name="close-circle-outline" size={16} color="#EF4444" />
            <Text className="text-sm ml-2" style={{ color: "#78350F" }}>
              Klik op de X om een lid te verwijderen
            </Text>
          </View>
          <View className="flex-row items-center">
            <Ionicons name="hourglass-outline" size={16} color="#D97706" />
            <Text className="text-sm ml-2" style={{ color: "#78350F" }}>
              Pre-registratie — wordt automatisch lid bij aanmelding
            </Text>
          </View>
        </View>

        <View className="h-4" />
      </ScrollView>

      {/* Add Member Modal */}
      <Modal visible={showAddModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface }}>
          {/* Modal Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
          >
            <Text className="text-lg font-semibold" style={{ color: colors.text }}>
              Lid Toevoegen
            </Text>
            <Pressable
              onPress={() => {
                setShowAddModal(false);
                setSearchQuery("");
              }}
              className="p-2"
              accessibilityRole="button"
              accessibilityLabel="Sluiten"
            >
              <Ionicons name="close" size={24} color={colors.textSecondary} />
            </Pressable>
          </View>

          {/* Search */}
          <View className="px-4 py-3">
            <View
              className="rounded-xl flex-row items-center px-3"
              style={{ backgroundColor: colors.background }}
            >
              <Ionicons name="search" size={20} color={colors.textTertiary} />
              <TextInput
                className="flex-1 py-3 px-2"
                style={{ color: colors.text }}
                placeholder="Zoek op naam of email..."
                placeholderTextColor={colors.textTertiary}
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
              {searchQuery.length > 0 && (
                <Pressable onPress={() => setSearchQuery("")}>
                  <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
                </Pressable>
              )}
            </View>
          </View>

          {/* Stats */}
          <View className="px-4 pb-2">
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              {filteredAvailableUsers.length} gebruiker{filteredAvailableUsers.length !== 1 ? "s" : ""}
              {filteredAvailablePending.length > 0 &&
                ` · ${filteredAvailablePending.length} pre-registratie${filteredAvailablePending.length !== 1 ? "s" : ""}`}
            </Text>
          </View>

          {/* Available Users List */}
          <ScrollView className="flex-1 px-4">
            {/* Registered Users */}
            {filteredAvailableUsers.length > 0 && (
              <Text
                className="text-xs font-semibold uppercase tracking-wide mb-2 mt-1"
                style={{ color: colors.textSecondary }}
              >
                Geregistreerde gebruikers
              </Text>
            )}
            {filteredAvailableUsers.map((profile) => {
              const isDependent = (profile as any).is_dependent === true;
              return (
                <Pressable
                  key={profile.id}
                  onPress={() => handleAddMember(profile)}
                  disabled={isAdding === profile.id}
                  className="flex-row items-center py-3"
                  style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
                  accessibilityRole="button"
                  accessibilityLabel={`Voeg ${profile.full_name || "gebruiker"} toe`}
                >
                  <View
                    className="w-12 h-12 rounded-full items-center justify-center mr-3"
                    style={{ backgroundColor: isDependent ? "#DBEAFE" : colors.background }}
                  >
                    <Text className="font-medium" style={{ color: isDependent ? "#1D4ED8" : colors.textSecondary }}>
                      {profile.full_name?.charAt(0).toUpperCase() || "?"}
                    </Text>
                  </View>
                  <View className="flex-1">
                    <View className="flex-row items-center">
                      <Text className="font-medium" style={{ color: colors.text }}>
                        {profile.full_name || "Onbekend"}
                      </Text>
                      {isDependent && (
                        <View className="rounded px-1.5 py-0.5 ml-2" style={{ backgroundColor: "#DBEAFE" }}>
                          <Text style={{ color: "#1D4ED8" }} className="text-xs font-medium">
                            Kind
                          </Text>
                        </View>
                      )}
                    </View>
                    <Text className="text-sm" style={{ color: colors.textTertiary }}>
                      {isDependent ? "Geen e-mail" : profile.email}
                    </Text>
                  </View>
                  {isAdding === profile.id ? (
                    <ActivityIndicator color="#D97706" />
                  ) : (
                    <View className="bg-amber-100 rounded-full p-2">
                      <Ionicons name="add" size={20} color="#D97706" />
                    </View>
                  )}
                </Pressable>
              );
            })}

            {/* Pre-registraties */}
            {filteredAvailablePending.length > 0 && (
              <>
                <Text
                  className="text-xs font-semibold uppercase tracking-wide mb-2 mt-6"
                  style={{ color: colors.textSecondary }}
                >
                  Pre-registraties
                </Text>
                {filteredAvailablePending.map((pending) => (
                  <Pressable
                    key={pending.id}
                    onPress={() => handleAssignPending(pending)}
                    disabled={isAssigningPending === pending.id}
                    className="flex-row items-center py-3"
                    style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
                    accessibilityRole="button"
                    accessibilityLabel={`Wijs ${pending.full_name} toe aan team`}
                  >
                    <View
                      className="w-12 h-12 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: "#FEF3C7" }}
                    >
                      <Ionicons name="hourglass-outline" size={20} color="#D97706" />
                    </View>
                    <View className="flex-1">
                      <View className="flex-row items-center">
                        <Text className="font-medium" style={{ color: colors.text }}>
                          {pending.full_name}
                        </Text>
                        <View className="rounded px-1.5 py-0.5 ml-2" style={{ backgroundColor: "#FEF3C7" }}>
                          <Text style={{ color: "#D97706" }} className="text-xs font-medium">
                            Pre-registratie
                          </Text>
                        </View>
                      </View>
                      <Text className="text-sm" style={{ color: colors.textTertiary }}>
                        {pending.email}
                      </Text>
                    </View>
                    {isAssigningPending === pending.id ? (
                      <ActivityIndicator color="#D97706" />
                    ) : (
                      <View className="bg-amber-100 rounded-full p-2">
                        <Ionicons name="add" size={20} color="#D97706" />
                      </View>
                    )}
                  </Pressable>
                ))}
              </>
            )}

            {filteredAvailableUsers.length === 0 && filteredAvailablePending.length === 0 && (
              <View className="items-center py-12">
                <Ionicons name="search-outline" size={48} color={colors.textTertiary} />
                <Text className="mt-2 text-center" style={{ color: colors.textTertiary }}>
                  {searchQuery
                    ? "Geen gebruikers gevonden"
                    : "Alle gebruikers zijn al in een team"}
                </Text>
              </View>
            )}
            <View className="h-8" />
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Guardian Assignment Modal */}
      <Modal visible={showGuardianModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface }}>
          {/* Modal Header */}
          <View
            className="flex-row items-center justify-between px-4 py-3"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
          >
            <View>
              <Text className="text-lg font-semibold" style={{ color: colors.text }}>
                Guardians Beheren
              </Text>
              {selectedDependent && (
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  voor {selectedDependent.profile.full_name}
                </Text>
              )}
            </View>
            <Pressable
              onPress={() => {
                setShowGuardianModal(false);
                setSelectedDependent(null);
              }}
              className="p-2"
              accessibilityRole="button"
              accessibilityLabel="Sluiten"
            >
              <Ionicons name="close" size={24} color={colors.textSecondary} />
            </Pressable>
          </View>

          {/* Info Banner */}
          <View className="px-4 py-3" style={{ backgroundColor: "#EDE9FE" }}>
            <View className="flex-row">
              <Ionicons name="information-circle" size={20} color="#7C3AED" />
              <Text className="flex-1 text-sm ml-2" style={{ color: "#5B21B6" }}>
                Selecteer teamleden die als guardian kunnen inloggen voor dit kind.
                Guardians kunnen "inloggen als" dit kind om mee te doen.
              </Text>
            </View>
          </View>

          {/* Available Guardians (non-dependent team members) */}
          <ScrollView className="flex-1 px-4 pt-4">
            <Text
              className="text-sm font-semibold uppercase tracking-wide mb-2"
              style={{ color: colors.textSecondary }}
            >
              Teamleden
            </Text>

            {members.filter((m) => !m.profile.is_dependent).length === 0 ? (
              <View className="items-center py-12">
                <Ionicons name="alert-circle-outline" size={48} color={colors.textTertiary} />
                <Text className="mt-2 text-center" style={{ color: colors.textTertiary }}>
                  Geen teamleden beschikbaar als guardian
                </Text>
                <Text className="text-sm text-center mt-1" style={{ color: colors.textTertiary }}>
                  Voeg eerst volwassenen toe aan het team
                </Text>
              </View>
            ) : (
              <View
                className="rounded-xl overflow-hidden"
                style={{ borderWidth: 1, borderColor: colors.border }}
              >
                {members
                  .filter((m) => !m.profile.is_dependent)
                  .map((member, index, arr) => {
                    const isGuardian = selectedDependent?.profile.guardian_ids?.includes(member.profile.id) || false;

                    return (
                      <Pressable
                        key={member.id}
                        onPress={() => handleToggleGuardian(member.profile.id)}
                        disabled={isSavingGuardians}
                        className="flex-row items-center px-4 py-3 active:opacity-70"
                        style={{
                          backgroundColor: isGuardian ? "#EDE9FE" : colors.surface,
                          borderBottomWidth: index < arr.length - 1 ? 1 : 0,
                          borderBottomColor: colors.border,
                        }}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isGuardian }}
                        accessibilityLabel={`${member.profile.full_name} als guardian`}
                      >
                        <View
                          className="w-10 h-10 rounded-full items-center justify-center mr-3"
                          style={{ backgroundColor: isGuardian ? "#7C3AED" : colors.background }}
                        >
                          <Text
                            className="font-medium"
                            style={{ color: isGuardian ? "#FFFFFF" : colors.textSecondary }}
                          >
                            {member.profile.full_name?.charAt(0).toUpperCase() || "?"}
                          </Text>
                        </View>
                        <View className="flex-1">
                          <Text className="font-medium" style={{ color: colors.text }}>
                            {member.profile.full_name}
                          </Text>
                          <Text className="text-sm" style={{ color: colors.textTertiary }}>
                            {member.profile.email}
                          </Text>
                        </View>
                        {isSavingGuardians ? (
                          <ActivityIndicator size="small" color="#7C3AED" />
                        ) : (
                          <Ionicons
                            name={isGuardian ? "checkmark-circle" : "ellipse-outline"}
                            size={24}
                            color={isGuardian ? "#7C3AED" : colors.textTertiary}
                          />
                        )}
                      </Pressable>
                    );
                  })}
              </View>
            )}

            {/* Selected Count */}
            {selectedDependent && (
              <View className="mt-4 p-3 rounded-xl" style={{ backgroundColor: colors.background }}>
                <Text className="text-center" style={{ color: colors.textSecondary }}>
                  {selectedDependent.profile.guardian_ids?.length || 0} guardian(en) geselecteerd
                </Text>
              </View>
            )}

            <View className="h-8" />
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}
